/**
 * @fileoverview The range the org Dashboard reads over: past 30 days, past 12
 * months, this year, last year, all time, or two dates of the director's own.
 *
 * A dropdown rather than a row of pills: it sits beside the tab bar, and six
 * ranges side by side there would read as a second set of tabs. A custom range
 * is only applied once both dates are real and in order, so a half-typed date
 * never sends the page off to fetch a range nobody asked for.
 */

import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RANGES } from "@/lib/orgReporting";

const TRIGGER =
  "h-9 w-auto min-w-[160px] gap-2 rounded-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 font-sans text-[13px] text-[var(--text-primary)]";
const DATE =
  "h-9 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 font-sans text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

/**
 * @param {{
 *   value: {id: string, start: string|null, end: string|null},
 *   onChange: (range: {id: string, start?: string, end?: string}) => void,
 *   max?: string,
 * }} props
 */
export default function DashboardRangePicker({ value, onChange, max }) {
  const [custom, setCustom] = useState(value.id === "custom");
  const [from, setFrom] = useState(value.start || "");
  const [to, setTo] = useState(value.end || max || "");

  // The URL is the source of truth: Back, or a reload, can change the range.
  useEffect(() => {
    setCustom(value.id === "custom");
    if (value.id === "custom") {
      setFrom(value.start || "");
      setTo(value.end || "");
    }
  }, [value.id, value.start, value.end]);

  const commit = (nextFrom, nextTo) => {
    if (nextFrom && nextTo && nextFrom <= nextTo) onChange({ id: "custom", start: nextFrom, end: nextTo });
  };

  const invalid = custom && from && to && from > to;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-range">
      <Select
        value={custom ? "custom" : value.id}
        onValueChange={(id) => {
          if (id === "custom") {
            setCustom(true);
            commit(from, to);
          } else {
            setCustom(false);
            onChange({ id });
          }
        }}
      >
        <SelectTrigger data-testid="dashboard-range-select" aria-label="Date range" className={TRIGGER}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
          {RANGES.map((r) => (
            <SelectItem key={r.id} value={r.id} className="font-sans text-[13px]">
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {custom && (
        <>
          <label className="sr-only" htmlFor="dashboard-range-from">
            From
          </label>
          <input
            id="dashboard-range-from"
            type="date"
            value={from}
            max={to || max}
            onChange={(e) => {
              setFrom(e.target.value);
              commit(e.target.value, to);
            }}
            data-testid="dashboard-range-from"
            className={DATE}
          />
          <span className="font-sans text-[13px] text-[var(--text-tertiary)]">to</span>
          <label className="sr-only" htmlFor="dashboard-range-to">
            To
          </label>
          <input
            id="dashboard-range-to"
            type="date"
            value={to}
            min={from || undefined}
            max={max}
            onChange={(e) => {
              setTo(e.target.value);
              commit(from, e.target.value);
            }}
            data-testid="dashboard-range-to"
            className={DATE}
          />
          {invalid && (
            <span role="alert" className="font-sans text-[12px] text-[var(--state-danger)]">
              The start date is after the end date.
            </span>
          )}
        </>
      )}
    </div>
  );
}
